import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InlineStatus from "@/components/InlineStatus";
import { ENTREGA, EMPAQUE } from "@/lib/statuses";
import { toast } from "sonner";
import { PackageCheck, Search, StickyNote, ExternalLink, Truck, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { isArchived, historyCounts } from "@/lib/archive";
import HistoryCounter from "@/components/HistoryCounter";
import EditableNote from "@/components/EditableNote";
import { ColocacionBadge, LiquidacionBadge } from "@/components/ColocacionBadge";
import { sortManual, useManualOrder, MoveHandle, PRIO_ROW } from "@/lib/ordenManual";
import { PrioBadge } from "@/components/PrioridadToggle";

export default function Empaque() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("activos");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState({});
  // Paginación en servidor (30 por página).
  const [page, setPage] = useState(1);
  const [pageMeta, setPageMeta] = useState({ total: 0, total_pages: 1, page: 1 });

  const load = async () => {
    try {
      // OPTIMIZACIÓN: filtrar en servidor + paginar. Antes cargaba TODOS los pedidos departamento.
      const params = { tipo: "departamento", page, page_size: 30 };
      if (view === "historial") params.estado_entrega = "entregado";
      const { data } = await api.get("/orders", { params });
      const raw = Array.isArray(data) ? data : (data.items || []);
      const relevant = raw.filter((o) => o.estado !== "cancelado");
      setOrders(relevant);
      setPageMeta(Array.isArray(data)
        ? { total: relevant.length, total_pages: 1, page: 1 }
        : { total: data.total || 0, total_pages: data.total_pages || 1, page: data.page || 1 });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [page, view]);
  useEffect(() => { setPage(1); }, [view]);

  const setSub = async (id, valor) => {
    try {
      await api.patch(`/orders/${id}/subestado`, { campo: "estado_entrega", valor });
      toast.success("Estado actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const historicos = orders.filter((o) => o.estado_entrega === "entregado");
  const historialActivos = historicos.filter((o) => !isArchived(o, "estado_entrega_at"));
  const historialArchivados = historicos.filter((o) => isArchived(o, "estado_entrega_at"));

  const byView = view === "historial"
    ? (showArchived ? historicos : historialActivos)
    : orders.filter((o) => (o.estado_entrega || "pendiente") !== "entregado");
  const s = q.trim().toLowerCase();
  const filtered = s
    ? byView.filter((o) =>
        (o.numero || "").toLowerCase().includes(s) ||
        (o.cliente_nombre || "").toLowerCase().includes(s) ||
        (o.envio_nombre || "").toLowerCase().includes(s) ||
        (o.items || []).some((it) => (it.product_name || "").toLowerCase().includes(s))
      )
    : byView;

  const totales = {
    piezas: filtered.reduce((n, o) => n + (o.total_qty || 0), 0),
    monto: filtered.reduce((n, o) => n + (o.total || 0), 0),
    ciudad: filtered.filter((o) => o.envio_tipo === "ciudad").length,
    dpto: filtered.filter((o) => o.envio_tipo !== "ciudad").length,
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const ordered = sortManual(filtered, "empaque");
  const { move, handleProps, rowProps } = useManualOrder("empaque", ordered, setOrders, (page - 1) * 1000);
  const canMove = !s;
  const esPrio = (o) => !!(o.prioridad_empaque || o.prioridad_entrega);
  const selectedInView = filtered.filter((o) => selected[o.id]);
  const allInViewSelected = filtered.length > 0 && filtered.every((o) => selected[o.id]);
  const toggleAll = () => {
    if (allInViewSelected) {
      const copy = { ...selected };
      filtered.forEach((o) => { delete copy[o.id]; });
      setSelected(copy);
    } else {
      const copy = { ...selected };
      filtered.forEach((o) => { copy[o.id] = true; });
      setSelected(copy);
    }
  };
  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const exportSeleccion = () => {
    if (selectedInView.length === 0) {
      toast.error("Selecciona al menos un pedido para exportar");
      return;
    }
    const rows = [
      ["Pedido", "Fecha", "Cliente", "Teléfono", "Teléfono alt", "Dirección", "Departamento/Zona",
       "Agencia", "Productos", "Total", "Empaque", "Entrega"],
      ...selectedInView.map((o) => [
        o.numero,
        (o.fecha || "").slice(0, 10),
        o.cliente_nombre || "",
        o.cliente_telefono || "",
        o.cliente_telefono2 || "",
        (o.direccion || "").replace(/\r?\n/g, " "),
        o.envio_zona || "",
        o.envio_nombre || "",
        (o.items || []).map((it) => `${it.cantidad}× ${it.product_name}`).join(" | "),
        o.total ?? 0,
        o.estado_empaque || "pendiente",
        o.estado_entrega || "pendiente",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `entregas_departamento_${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success(`Exportado (${selectedInView.length} pedidos)`);
  };

  return (
    <div className="space-y-6" data-testid="empaque-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Preparación</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <PackageCheck className="w-8 h-8 text-[#F59E0B]" /> Entregas Departamento
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Lista de pedidos listos de fabricación, para empacar y entregar a la agencia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={exportSeleccion}
            className="bg-[#002FA7] hover:bg-[#00227A] gap-2 disabled:opacity-40"
            disabled={selectedInView.length === 0}
            data-testid="emp-export-btn"
          >
            <Download className="w-4 h-4" />
            Exportar selección
            <span className="text-[10px] bg-white/20 rounded-full px-1.5 py-0.5">{selectedInView.length}</span>
          </Button>
        </div>
      </header>

      {/* View tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 -mb-2">
        {[
          { v: "activos", label: "Activos", count: orders.filter((o) => (o.estado_entrega || "pendiente") !== "entregado").length },
          { v: "historial", label: "Historial (entregados a agencia)", count: historialActivos.length },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setView(t.v)}
            data-testid={`emp-view-${t.v}`}
            className={`px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              view === t.v ? "border-[#F59E0B] text-[#B45309] font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
            <span className="ml-1 text-[10px] bg-zinc-100 rounded-full px-1.5 py-0.5">{t.count}</span>
          </button>
        ))}
        {view === "historial" && historialArchivados.length > 0 && (
          <label className="ml-auto text-xs text-zinc-600 flex items-center gap-2 cursor-pointer" data-testid="emp-archive-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Incluir archivados <span className="text-zinc-400">({historialArchivados.length} de +1 día)</span>
          </label>
        )}
      </div>

      {/* Summary */}
      {view === "historial" && (
        <HistoryCounter counts={historyCounts(historicos, "estado_entrega_at")} label="Entregados a agencia" />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Por empacar</div>
          <div className="font-manrope font-black text-3xl mt-1">{filtered.filter((o) => !["empacado", "listo"].includes(o.estado_empaque)).length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Piezas</div>
          <div className="font-manrope font-black text-3xl mt-1">{totales.piezas}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Ciudad / Depto</div>
          <div className="font-manrope font-black text-3xl mt-1">{totales.ciudad} / {totales.dpto}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Monto</div>
          <div className="font-manrope font-black text-3xl mt-1">{formatMoney(totales.monto)}</div>
        </Card>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por pedido, cliente, envío o producto..." className="pl-9" data-testid="emp-search" />
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allInViewSelected}
                    onChange={toggleAll}
                    aria-label="Seleccionar todo"
                    data-testid="emp-select-all"
                  />
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Envío</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Productos</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Notas</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Empaque</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Acciones</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-zinc-400 py-12">
                    <PackageCheck className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                    No hay pedidos por empacar.
                  </td>
                </tr>
              )}
              {ordered.map((o, i) => (
                <tr key={o.id} {...rowProps(i)} className={`align-top ${esPrio(o) ? PRIO_ROW : `hover:bg-zinc-50 ${selected[o.id] ? "bg-blue-50/60" : ""}`}`} data-testid={`emp-row-${o.numero}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-1">
                    <input
                      type="checkbox"
                      checked={!!selected[o.id]}
                      onChange={() => toggle(o.id)}
                      aria-label={`Seleccionar ${o.numero}`}
                      data-testid={`emp-select-${o.numero}`}
                    />
                    {canMove && <MoveHandle i={i} n={ordered.length} move={move} handleProps={handleProps} testid={`emp-move-${o.numero}`} />}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-mono text-xs font-semibold">{o.numero}</div>
                    <PrioBadge show={esPrio(o)} />
                    <div className="text-[11px] text-zinc-500 mt-0.5">{formatDate(o.fecha)}</div>
                    {o.fecha_entrega && (
                      <div className="text-[11px] text-orange-700 mt-0.5">Entrega: {formatDate(o.fecha_entrega)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[160px]">
                    <div className="font-semibold">{o.cliente_nombre}</div>
                    {o.cliente_telefono && <div className="text-[11px] text-zinc-500">{o.cliente_telefono}{o.cliente_telefono2 ? <span className="text-zinc-400"> · alt {o.cliente_telefono2}</span> : null}</div>}
                  </td>
                  <td className="px-4 py-3 min-w-[140px]">
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <Truck className="w-3 h-3 text-zinc-500" />{o.envio_nombre || "—"}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 capitalize">{o.envio_tipo}</div>
                    {o.envio_zona && <div className="text-[11px] text-zinc-500 mt-0.5">{o.envio_zona}</div>}
                  </td>
                  <td className="px-4 py-3 min-w-[280px] max-w-md">
                    <ul className="space-y-1">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="text-xs flex items-start gap-2">
                          <span className="inline-block min-w-[28px] text-right font-mono font-bold text-zinc-900">{it.cantidad}×</span>
                          <span className="flex-1">
                            <span className="font-medium">{it.product_name}</span>
                            {(it.color || it.tamano || it.estilo) && (
                              <span className="text-zinc-500"> — {[it.color, it.tamano, it.estilo].filter(Boolean).join(" · ")}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                      {o.items?.length || 0} producto{o.items?.length === 1 ? "" : "s"} · {o.total_qty || 0} unidades
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-[220px] min-w-[180px]">
                    <EditableNote
                      orderId={o.id}
                      initial={o.notas_hoja || ""}
                      onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_hoja: v } : x))}
                    />
                    {o.notas && (
                      <div className="mt-1 text-[10px] text-zinc-500 italic border-l-2 border-zinc-300 pl-2">
                        <StickyNote className="w-3 h-3 inline mr-1" />
                        {o.notas}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_empaque"
                      value={o.estado_empaque || "pendiente"}
                      options={EMPAQUE}
                      canEdit={false}
                      onChanged={load}
                    />
                    <ColocacionBadge o={o} />
                    <LiquidacionBadge o={o} />
                  </td>
                  <td className="px-4 py-3">
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_entrega"
                      value={o.estado_entrega || "pendiente"}
                      options={ENTREGA}
                      canEdit
                      onChanged={load}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="outline" className="h-6 text-[9px] bg-yellow-50 border-yellow-200 text-yellow-900" onClick={() => setSub(o.id, "por_recolectar")} data-testid={`emp-porrec-${o.numero}`}>Por recolectar</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[9px] bg-blue-50 border-blue-200 text-blue-900" onClick={() => setSub(o.id, "en_ruta")} data-testid={`emp-enruta-${o.numero}`}>En ruta</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[9px] bg-indigo-50 border-indigo-200 text-indigo-900" onClick={() => setSub(o.id, "en_bodega")} data-testid={`emp-bodega-${o.numero}`}>En bodega</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[9px] bg-red-50 border-red-200 text-red-900" onClick={() => setSub(o.id, "danado")} data-testid={`emp-danado-${o.numero}`}>Dañado</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[9px] bg-orange-50 border-orange-200 text-orange-900" onClick={() => setSub(o.id, "no_recibido")} data-testid={`emp-norecib-${o.numero}`}>No recibido</Button>
                      <Button size="sm" className="h-6 text-[9px] bg-blue-600 hover:bg-blue-700" onClick={() => setSub(o.id, "entregado")} data-testid={`emp-listo-${o.numero}`}>Entregado</Button>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link to={`/pedidos/${o.id}`} onClick={() => { try { sessionStorage.setItem("pedidos_return_path", "/empaque"); } catch {} }} className="text-[#002FA7] text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-1">
                      Ver <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pageMeta.total_pages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-zinc-50" data-testid="empaque-paginacion">
              <div className="text-xs text-zinc-600 tabular-nums">
                Mostrando <b>{((pageMeta.page - 1) * 30) + 1}</b>–<b>{Math.min(pageMeta.page * 30, pageMeta.total)}</b> de <b>{pageMeta.total}</b>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageMeta.page <= 1} data-testid="emp-pag-prev">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                <span className="text-xs font-black tabular-nums">Pág. {pageMeta.page} / {pageMeta.total_pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageMeta.total_pages, p + 1))} disabled={pageMeta.page >= pageMeta.total_pages} data-testid="emp-pag-next">
                  Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile stacked list */}
        <div className="md:hidden divide-y divide-zinc-100">
          {filtered.length === 0 && (
            <div className="text-center text-zinc-400 py-12 text-sm">No hay pedidos por empacar.</div>
          )}
          {ordered.map((o, i) => (
            <div key={o.id} {...rowProps(i)} className={`p-4 ${esPrio(o) ? PRIO_ROW : ""}`} data-testid={`emp-card-${o.numero}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                {canMove && <MoveHandle i={i} n={ordered.length} move={move} handleProps={handleProps} testid={`emp-mmove-${o.numero}`} />}
                <div>
                  <div className="font-mono text-xs font-semibold">{o.numero}</div>
                  <PrioBadge show={esPrio(o)} />
                  <div className="font-semibold">{o.cliente_nombre}</div>
                  <div className="text-[11px] text-zinc-500 capitalize">{o.envio_nombre || "—"} · {o.envio_tipo}</div>
                </div>
                </div>
                <InlineStatus
                  orderId={o.id}
                  campo="estado_entrega"
                  value={o.estado_entrega || "pendiente"}
                  options={ENTREGA}
                  canEdit
                  onChanged={load}
                />
              </div>
              <ul className="mt-2 text-xs space-y-0.5">
                {(o.items || []).map((it, i) => (
                  <li key={i}><span className="font-mono font-bold">{it.cantidad}×</span> {it.product_name}
                    {(it.color || it.tamano || it.estilo) && (
                      <span className="text-zinc-500"> — {[it.color, it.tamano, it.estilo].filter(Boolean).join(" · ")}</span>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-2">
                <EditableNote
                  orderId={o.id}
                  initial={o.notas_hoja || ""}
                  onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_hoja: v } : x))}
                />
              </div>
              {o.notas && (
                <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <StickyNote className="w-3 h-3 inline mr-1 text-yellow-700" /> {o.notas}
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[10px] bg-yellow-50 border-yellow-200 text-yellow-900" onClick={() => setSub(o.id, "por_recolectar")}>Por recolectar</Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] bg-blue-50 border-blue-200 text-blue-900" onClick={() => setSub(o.id, "en_ruta")}>En ruta</Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] bg-indigo-50 border-indigo-200 text-indigo-900" onClick={() => setSub(o.id, "en_bodega")}>En bodega</Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] bg-red-50 border-red-200 text-red-900" onClick={() => setSub(o.id, "danado")}>Dañado</Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] bg-orange-50 border-orange-200 text-orange-900" onClick={() => setSub(o.id, "no_recibido")}>No recibido</Button>
                <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={() => setSub(o.id, "entregado")}>Entregado</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
